import React from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService } from '../../../../services/worker'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import {
  MobileCard,
  MobileChip,
  MobileEmptyState,
  MobilePrimaryButton,
  MobileSecondaryButton,
  MobileSectionTitle,
  MobileSheet,
  statusTone,
} from './WorkerMobilePrimitives'

/**
 * Time & pay — the web mirror of the Android "Time" tab. Every hour figure and
 * every pay label shown here is exactly what the backend returned; masked rates
 * stay masked, and no total is recomputed in the browser.
 */

const settlementLabel = (state: string | undefined, t: (key: string) => string) => {
  if (state === 'settled') return t('Settled')
  if (state === 'exported') return t('Exported to payroll')
  if (state === 'locked') return t('Locked for payroll')
  if (state === 'ready') return t('Ready for payroll')
  if (state === 'not_ready') return t('Not ready')
  return t('Not available')
}

export const WorkerMobileTime: React.FC<{ state: WorkerWorkspaceState }> = ({ state }) => {
  const { t, formatDate } = useI18n()
  const { data, busy, run } = state
  const [disputeFor, setDisputeFor] = React.useState<string | null>(null)
  const [disputeMessage, setDisputeMessage] = React.useState('')

  return (
    <div className="wm-screen" data-testid="worker-mobile-time">
      <MobileSectionTitle
        title={t('Time & pay')}
        detail={t('Submitted, adjusted, and final payable hours are calculated by your organization.')}
      />
      {data.timesheets.length ? data.timesheets.map((row) => (
        <MobileCard key={row.shiftId} testId="worker-mobile-timesheet">
          <div className="wm-card__heading">
            <div>
              <h3>{row.companyName || row.locationName || t('Completed shift')}</h3>
              <p>{row.shiftDate ? formatDate(row.shiftDate, { dateStyle: 'medium' }) : t('Date not available')}</p>
              {row.agencyName && row.agencyName !== row.companyName ? <small>{t('Supplied by {name}', { name: row.agencyName })}</small> : null}
            </div>
            <MobileChip tone={statusTone(row.approvalStatus)}>{t(String(row.approvalStatus).replace(/_/g, ' '))}</MobileChip>
          </div>
          <dl className="wm-facts">
            <div><dt>{t('Submitted')}</dt><dd>{row.submittedTotalHours} {t('hours')}</dd></div>
            <div><dt>{t('Breaks')}</dt><dd>{row.finalBreakMinutes ?? row.submittedBreakMinutes} {t('min')}</dd></div>
            <div><dt>{t('Final payable')}</dt><dd>{row.finalTotalHours ?? row.submittedTotalHours} {t('hours')}</dd></div>
            <div><dt>{t('Pay')}</dt><dd>{row.payLabel || (row.payVisibility === 'masked' ? t('Rate not shown') : '—')}</dd></div>
            <div><dt>{t('Payroll')}</dt><dd>{settlementLabel(row.settlement?.state, t)}</dd></div>
          </dl>
          {row.adminAdjustmentReason ? <p className="wm-guidance"><strong>{t('Adjustment')}:</strong> {row.adminAdjustmentReason}</p> : null}
          {row.rejectionReason ? <p className="wm-guidance"><strong>{t('Rejection')}:</strong> {row.rejectionReason}</p> : null}
          {row.workerDisputeMessage ? <p className="wm-guidance"><strong>{t('Your dispute')}:</strong> {row.workerDisputeMessage}</p> : null}
          {row.disputeResolutionNote ? <p className="wm-guidance"><strong>{t('Resolution')}:</strong> {row.disputeResolutionNote}</p> : null}
          <div className="wm-secondary-actions">
            <MobilePrimaryButton
              label={t('Accept adjustment')}
              testId="worker-mobile-accept-adjustment"
              busy={busy === `accept-${row.shiftId}`}
              disabled={Boolean(busy) || row.payrollLocked || row.approvalStatus !== 'adjusted_by_admin'}
              onClick={() => void run(`accept-${row.shiftId}`, () => workerService.acceptTimesheet(row.shiftId), t('Adjustment accepted.'))}
            />
            <MobileSecondaryButton
              label={t('Dispute')}
              testId="worker-mobile-dispute"
              disabled={Boolean(busy) || row.payrollLocked || !['adjusted_by_admin', 'rejected'].includes(row.approvalStatus)}
              onClick={() => { setDisputeFor(row.shiftId); setDisputeMessage('') }}
            />
          </div>
        </MobileCard>
      )) : (
        <MobileEmptyState title={t('No timesheets yet')} detail={t('Completed shifts appear here for you to review.')} />
      )}

      <MobileSheet
        title={t('Dispute this timesheet')}
        open={Boolean(disputeFor)}
        onClose={() => setDisputeFor(null)}
        testId="worker-mobile-dispute-sheet"
      >
        <form
          className="wm-form"
          onSubmit={(event) => {
            event.preventDefault()
            const shiftId = disputeFor
            const message = disputeMessage.trim()
            if (!shiftId || !message) return
            setDisputeFor(null)
            void run(`dispute-${shiftId}`, () => workerService.disputeTimesheet(shiftId, message), t('Dispute submitted.'))
          }}
        >
          <label className="wm-field">
            <span>{t('Describe the issue with this timesheet')}</span>
            <textarea rows={4} value={disputeMessage} onChange={(event) => setDisputeMessage(event.target.value)} required />
          </label>
          <button type="submit" className="wm-button wm-button--primary" data-tone="info" disabled={!disputeMessage.trim()}>
            {t('Submit dispute')}
          </button>
        </form>
      </MobileSheet>
    </div>
  )
}
